import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { ApiService } from 'src/app/core/services/api.service';
import { RolesEnum } from 'src/app/features/tests/features/constants';

interface UserItem {
  value: string;
  viewValue: string;
}

@Component({
  selector: 'app-users-select',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    FormsModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatAutocompleteModule,
    MatIconModule,
  ],
  templateUrl: './users-select.component.html',
  styleUrls: ['./users-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UsersSelectComponent),
      multi: true,
    },
  ],
})
export class UsersSelectComponent
  implements ControlValueAccessor, OnInit, OnChanges
{
  @Output() userSelected = new EventEmitter<string>();
  @Input() selectedValue: string | null = null;
  @Input() selectedDepartment: string | null = null;
  @Input() includeUnassigned?: boolean = undefined;
  @Input() required?: boolean = false;
  @Input() label: string = 'User';
  @Input() disabled?: boolean = false;
  @Input() role?: RolesEnum | string = RolesEnum.Physician;
  // Renders a type-ahead field instead of a plain dropdown. Opt-in so existing
  // usages keep the dropdown they were built around.
  @Input() searchable: boolean = false;
  @Input() searchPlaceholder: string = 'Search';
  // Display name for the already-assigned user. Needed when that user is not in
  // this role's list — the field would otherwise render empty.
  @Input() selectedLabel: string | null = null;
  selectedUser: UserItem | null = null;
  loading: boolean = false;
  constructor(private apiService: ApiService, private dialog: MatDialog) {}
  users: UserItem[] = [];
  filteredUsers: UserItem[] = [];
  onChange: (value: string) => void = () => {};
  onTouched: () => void = () => {};

  writeValue(value: string): void {
    this.selectedValue = value;
    this.syncSelectedUser();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  ngOnInit() {
    this.loadUsers();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['selectedDepartment'] &&
      !changes['selectedDepartment'].firstChange
    ) {
      this.users = [];
      this.filteredUsers = [];
      this.loadUsers();
    }
    if (changes['selectedValue'] && !changes['selectedValue'].firstChange) {
      this.syncSelectedUser();
    }
  }

  loadUsers() {
    this.loading = true;
    const query= {
          role: this.role,
          limit: 100,
          ...(this.selectedDepartment
            ? { departmentId: this.selectedDepartment }
            : {}),
        }
    if(this.includeUnassigned) {
      query['includeUnassigned'] = true
    }
    this.apiService
      .request({
        path: `users/dropdown`,
        query: query,
        method: 'GET',
      })
      .subscribe({
        next: (data) => {
          this.users = data.items.map((m) => {
            return {
              viewValue: m.fullName,
              value: String(m.id),
              phone: m.phone,
              fax: m.fax,
            };
          });
          if (!this.required) {
            this.users = [{ viewValue: 'All', value: '' }, ...this.users];
          }
          this.ensureSelectedOption();
          this.filteredUsers = [...this.users];
          if (this.selectedValue) {
            this.selectUserItem(this.selectedValue);
          }
        },
        error: (error) => {
          console.error('Error loading Users', error);
        },
        complete: () => {
          this.loading = false;
          console.log('Users loaded successfully');
        },
      });
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  selectUserItem(value: string) {
    this.selectedValue = value;
    this.onChange(value);
    this.userSelected.emit(value);
    this.selectedUser = this.users.find((d) => d.value === value) || null;
    this.filteredUsers = [...this.users];
  }

  // Filters the loaded page of users as the field is typed in. The argument is
  // the typed text while searching, and the picked user once one is chosen.
  filterUsers(event: UserItem | string): void {
    const search =
      typeof event === 'string'
        ? event.toLowerCase()
        : event?.viewValue?.toLowerCase() || '';
    this.filteredUsers = this.users.filter((u) =>
      u.viewValue?.toLowerCase().includes(search),
    );
  }

  displayUser(user: UserItem): string {
    return user?.viewValue || '';
  }

  private syncSelectedUser(): void {
    this.ensureSelectedOption();
    this.selectedUser =
      this.users.find((u) => u.value === this.selectedValue) || null;
  }

  // The assigned user is not necessarily one this role's list can return — a
  // report generated by an admin, for instance, assigns them as the test's
  // interpreting physician. Keep them listed so the field shows who is set.
  private ensureSelectedOption(): void {
    if (!this.selectedValue || !this.selectedLabel || !this.users.length) {
      return;
    }
    const value = String(this.selectedValue);
    if (this.users.some((u) => u.value === value)) {
      return;
    }
    this.users = [...this.users, { value, viewValue: this.selectedLabel }];
  }
}
